import React, {
  memo,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  type UIEvent
} from 'react';
import {
  MRTDataTable,
  Accordion,
  Button,
  ButtonVariant,
  Checkbox,
  ComboboxOption,
  DatePicker,
  FlyoutContainer,
  CmhcIcon,
  CmhcIconSize,
  RadioButton,
  AutoCompleteDropDown,
  MouseOrKeyboardEvent,
  CmhcFlexIcon,
  ButtonSize,
  ToastNotification,
  ToastNotificationVariant,
  NotificationBanner
} from '@cmhc-schl/hearth-ui';
import { Box, Stack, Chip, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useInventoryFilters } from 'Feature/V2/Shared_Portfolio/Hooks/useInventoryFilters';
import { useAuthenticationContext } from 'Foundation/Authentication';
import i18n from 'i18next';
import { MRT_Row, MRT_SortingState } from 'material-react-table';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  Status,
  RequestType,
  premiumDiscountOption,
  fraudDetectionIndicatorOption
} from '../../constants';
import { requestTypeMapping } from '../../utils/RequestTypeMapping';
import {
  TableProps,
  LenderOptionType,
  MasterPoolTableFields,
  FormFilterErrors,
  LatestRemark
} from '../HbtMasterPool/types';
import {
  getInsuredApprovedLenderList,
  getValidationInventoryTableData
} from '../HbtMasterPool/ValidationInventoryService';
import { createLoansInventoryColumns } from './columns/masterPoolInventoryTableColumns';
import styles from './styles.module.scss';
import { DropdownOptionType, FilterValuesType } from './types';
import { getFilterDropdownValues } from './utils/GetFilterDropdownValues';
import { getRequestTypeLabel } from './utils/GetRequestTypeLabelUtils';
import { initialFilterValues } from './utils/InitialDefaultValues';
import { loanStatusMapping } from './utils/LoanStatusMapping';
import { premiumDiscountMapping } from './utils/PremiumDiscountOptionsMapping';
import { fraudDetectionIndicatorMapping } from './utils/FraudDetectionIndicatorOptionsMapping';
import { navigateToMasterPoolDetails } from './utils/NavigateToMasterPoolDetails';
import { buildSearchFilterApiQuery } from 'Feature/V2/Shared_Portfolio/utils/buildSearchFilterApiQuery';
import { getSearchParams } from 'Feature/V2/Shared_Portfolio/components/SearchFlyout/utils/getSearchParams';
import { getLabel } from 'Feature/V2/Shared_Portfolio/components/SearchFlyout/utils/getLabel';
import { useMRTCustomTranslation } from 'Feature/V2/Shared_Portfolio/Hooks/useMRTCustomTransaltion';
import { removeDuplicateObjects } from 'Feature/V2/Shared_Portfolio/utils/removeDuplicateObjects';
import ViewHistoricalRemarksFlyout from '../HbtMasterPoolLoansInventoryTable/components/ViewHistoricalRemarksFlyout/ViewHistoricalRemarksFlyout';
import HbtMasterPoolNumberAddRemarksFlyout from '../HbtMasterPool/component/HbtMasterPoolNumberAddRemarks';
import { ToastNotificationConfig } from '../HbtMasterPoolDetails/types';
import { NotificationBannerType } from 'Feature/V2/HbtPIRequest/components/HbtPIRequestPage/types';
import { RowActions } from './components/rowActions';
import { RemarkItem } from '../HbtMasterPoolLoansInventoryTable/types';
import { getMasterPoolHistoricalRemarks } from '../HbtMasterPoolLoansInventoryTable/components/ViewHistoricalRemarksFlyout/api/MasterPoolNumberViewHistoricalRemarksService';

const fetchSize = 100;

const HbtMasterPoolTable: React.FC<TableProps> = ({
  openFlyout,
  toggleDrawer,
  updateFilters,
  toggleSearchDrawer,
  onDataStatusChange
}) => {
  const authenticationContext = useAuthenticationContext();
  const [formErrors, setFormErrors] = useState<FormFilterErrors>({});
  const [fromDateInput, setFromDateInput] = useState<Date | null>(null);
  const [toDateInput, setToDateInput] = useState<Date | null>(null);
  const { t } = useTranslation('HbtMasterPool');

  const [selectedFilters, setSelectedFilters] = useState({});
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [selectedRow, setSelectedRow] = useState({
    masterPoolNumber: null as number | null,
    latestRemark: {} as LatestRemark
  });

  const [showErrorBannerNotification, setShowErrorBannerNotification] = useState<boolean>(false);
  const [showToast, setShowToast] = useState<boolean>(false);

  const [toastNotificationConfig, setToastNotificationConfig] = useState<ToastNotificationConfig>({
    icon: 'icon_check_circle',
    header: '',
    subHeader: String(' ')
  });
  const [notificationBannerConfig, setNotificationBannerConfig] = useState<ToastNotificationConfig>(
    { header: '', subHeader: ' ' }
  );
  const [isDataTableErrorDismissed, setIsDataTableErrorDismissed] = useState<boolean>(false);

  const handleToastNotification = (icon: string, message: string, desc = '') => {
    setToastNotificationConfig({
      icon,
      header: message,
      subHeader: desc
    });

    setShowToast(true);
  };

  const [openMasterPoolNumberRemarksFlyout, setOpenMasterPoolNumberRemarksFlyout] =
    useState<boolean>(false);

  const toggleMasterPoolNumberRemarksDrawer = (newOpen: boolean) => {
    setOpenMasterPoolNumberRemarksFlyout(newOpen);
    showToast && newOpen && setShowToast(false);
    showErrorBannerNotification && newOpen && setShowErrorBannerNotification(false);
  };

  const [openHistoricalRemarksFlyout, setOpenHistoricalRemarksFlyout] = useState<boolean>(false);
  const [selectedMasterPoolForRemark, setSelectedMasterPoolForRemark] = useState<(number | null)[]>(
    []
  );

  const selectedMasterPoolNumber = useMemo(
    () => selectedMasterPoolForRemark.find((value) => typeof value === 'number'),
    [selectedMasterPoolForRemark]
  );

  const {
    data: remarksResponse,
    isLoading: isRemarksLoading,
    isError: isRemarksError
  } = useQuery<RemarkItem[]>({
    queryKey: ['masterpool-number-historical-remarks', selectedMasterPoolNumber],
    queryFn: () =>
      getMasterPoolHistoricalRemarks({
        authenticationContext,
        masterPoolNumber: selectedMasterPoolNumber!
      }),
    enabled: typeof selectedMasterPoolNumber === 'number'
  });

  const toggleHistoricalRemarksDrawer = (newOpen: boolean) => {
    setOpenHistoricalRemarksFlyout(newOpen);
  };

  const history = useHistory();
  const {
    activeFilters,
    changeFilter: handleFilterChange,
    clearFilter,
    filterValues,
    deleteChip,
    getChips: getActiveFilterChips,
    setActiveFilters,
    setFilterValues
  } = useInventoryFilters({
    initialFilterValues,
    translation: 'HbtMasterPool'
  });

  const stableFilters = useMemo(
    () => ({
      ...selectedFilters
    }),
    [selectedFilters]
  );

  const { dataType, searchQuery } = getSearchParams(location.search);

  const [premiumLocal, setPremiumLocal] = useState<string | undefined>(
    filterValues.premiumDiscountIndicator
  );

  const [fraudDetectionLocal, setFraudDetectionLocal] = useState<string | undefined>(
    filterValues.fraudDetectionIndicator
  );

  const fetchInventoryData = async ({ pageParam = 0 }) => {
    const offset = pageParam;

    let sortField = sorting[0]?.id ?? '';
    if (sortField === 'insuredApprovedLenderName') {
      sortField =
        i18n.language === 'en' ? 'insuredApprovedEnglishName' : 'insuredApprovedFrenchName';
    }
    const sortOrder = !!sorting[0]?.desc;

    const sizeQueryParams = `?skip=${offset}&take=${fetchSize}`;
    const filterQueryParams = buildSearchFilterApiQuery(selectedFilters);
    const { searchQuery } = getSearchParams(location.search);
    const searchQueryParams = searchQuery ? buildSearchFilterApiQuery(searchQuery) : '';

    const sortFieldQueryParams = `${
      sortField ? `&orderBy=${sortField} ${sortOrder ? 'desc' : 'asc'}` : ''
    }`;

    const queryParams = `${sizeQueryParams}${filterQueryParams}${searchQueryParams}${sortFieldQueryParams}`;

    const data = await getValidationInventoryTableData({
      authenticationContext,
      queryParams
    });
    return data;
  };

  const {
    data,
    fetchNextPage,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    error: dataTableError
  } = useInfiniteQuery({
    queryKey: ['master-pool-inventory-table', stableFilters, sorting, location.search],
    queryFn: fetchInventoryData,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.flat().length;
      const hasMore = lastPage.length === fetchSize;
      return hasMore ? totalFetched : undefined;
    }
  });

  const showDataTableErrorBanner = !!dataTableError && !isDataTableErrorDismissed;

  const dataTableErrorBannerConfig = useMemo<Pick<ToastNotificationConfig, 'header' | 'subHeader'>>(
    () => ({
      header: t('PI-Master-Pool-Premiums-Taxes-API-Error-Header'),
      subHeader: t('Master-Pool-Table-Notification-Error-Body')
    }),
    [t]
  );

  const flatData = useMemo(() => data?.pages.flat() ?? [], [data]);
  const mrtCustomTranslation = useMRTCustomTranslation(flatData.length);

  useEffect(() => {
    if (dataTableError) {
      setIsDataTableErrorDismissed(false);
    }
  }, [dataTableError]);

  useEffect(() => {
    if (onDataStatusChange) {
      if (flatData.length === 0) {
        onDataStatusChange(false);
      } else {
        onDataStatusChange(true);
      }
    }
  }, [flatData, onDataStatusChange]);

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (scrollHeight - scrollTop - clientHeight < 400 && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      }
    },
    [fetchNextPage, isFetchingNextPage, hasNextPage]
  );

  const { data: filterDropdownData } = useQuery({
    queryKey: ['master-pool-approved-lender'],
    queryFn: async () => getInsuredApprovedLenderList(authenticationContext),
    initialData: [] as LenderOptionType[] | null
  });

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);

  const columns = useMemo(
    () =>
      createLoansInventoryColumns(
        t,
        i18n.language,
        setSelectedMasterPoolForRemark,
        toggleHistoricalRemarksDrawer
      ),
    [t, i18n.language]
  );

  const handleClearFilter = () => {
    clearFilter();
    setFormErrors({});
    setSelectedFilters({});
    setFromDateInput(null);
    setToDateInput(null);
    setPremiumLocal(undefined);
    setFraudDetectionLocal(undefined);
  };

  const handleFilterSubmit = async (
    activeFilters?: FilterValuesType,
    nextFilterValues?: FilterValuesType
  ) => {
    const currentFilterValue = activeFilters || nextFilterValues || filterValues;
    try {
      let fromDate =
        (fromDateInput && moment(fromDateInput).format('YYYY-MM-DD')) ??
        currentFilterValue.startDate;
      let toDate =
        (toDateInput && moment(toDateInput).format('YYYY-MM-DD')) ?? currentFilterValue.endDate;
      const fromDateMoment = moment(fromDate);
      const toDateMoment = moment(toDate);
      fromDate = fromDateMoment.isValid() ? fromDate : undefined;
      toDate = toDateMoment.isValid() ? toDate : undefined;

      const today = moment().endOf('day');

      const errors: FormFilterErrors = {};

      if (fromDate && !toDate) {
        errors.toDate = true;
      }
      if ((!fromDate && toDate) || fromDateMoment.isAfter(today)) {
        errors.fromDate = true;
      }

      if (fromDate && toDate && toDateMoment.isBefore(fromDateMoment)) {
        errors.toDate = true;
        errors.dateRange = t('Pi-Portfolio-Management-Table-Filter-Date-DateRange-Error-Message');
      }
      if (
        !currentFilterValue.ialn &&
        !currentFilterValue.masterPoolNumber &&
        currentFilterValue.premiumDiscountIndicator === '' &&
        !currentFilterValue.requestType &&
        !currentFilterValue.status &&
        !fromDate &&
        !toDate &&
        currentFilterValue.fraudDetectionIndicator === ''
      ) {
        toggleDrawer(false);
        return;
      }
      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }
      setFormErrors({});
      if (!activeFilters) {
        setActiveFilters({
          ...currentFilterValue,
          startDate: fromDate ?? undefined,
          endDate: toDate ?? undefined
        });
      }
      const filter = {
        requestType: currentFilterValue.requestType ?? [],
        status: currentFilterValue.status?.map((item: DropdownOptionType) => item.value) ?? [],
        insuredApprovedLender:
          currentFilterValue.ialn?.map((item: DropdownOptionType) => item.value) ?? [],
        requestReceiptDateFrom: fromDate,
        requestReceiptDateTo: toDate,
        masterPoolCmhcAccountNumber:
          currentFilterValue.masterPoolNumber?.map((item: DropdownOptionType) => +item.value) ?? [],
        fraudDetectionIndicator: currentFilterValue.fraudDetectionIndicator,
        premiumDiscountIndicator: currentFilterValue.premiumDiscountIndicator
      };
      setSelectedFilters(filter);
      setFilterValues({
        ...currentFilterValue,
        startDate: fromDate ?? undefined,
        endDate: toDate ?? undefined
      });
      toggleDrawer(false);
    } catch (error) {
      console.error('Error fetching filtered data', error);
    }
  };

  const handleChipDelete = (chip: { key: string }) => {
    const { nextActiveFilters, nextFiltersValues } = deleteChip(chip);

    getActiveFilterChips().length === 1
      ? handleClearFilter()
      : handleFilterSubmit(nextActiveFilters, nextFiltersValues);
  };

  useEffect(() => {
    updateFilters(getActiveFilterChips());
  }, [activeFilters]);

  const lenderList = useMemo(() => {
    const flatLenderList = filterDropdownData && removeDuplicateObjects(filterDropdownData);
    return getFilterDropdownValues<LenderOptionType>(
      flatLenderList,
      undefined,
      i18n.language === 'en' ? 'approvedLenderEnglishName' : 'approvedLenderFrenchName',
      'lenderCode'
    );
  }, [filterDropdownData]);

  const masterPoolNumbersList = useMemo(
    () =>
      getFilterDropdownValues(
        Array.from(new Set(flatData.map((row) => String(row.masterPoolCmhcAccountNumber)))),
        undefined
      ),
    [flatData]
  );

  const premiumDiscountOptions = useMemo(
    () => getFilterDropdownValues(premiumDiscountOption, premiumDiscountMapping(t)),
    []
  );

  const fraudDetectionOptions = useMemo(
    () => getFilterDropdownValues(fraudDetectionIndicatorOption, fraudDetectionIndicatorMapping(t)),
    []
  );

  const onPremiumChange = useCallback(
    (value?: string) => {
      setPremiumLocal(value);
      handleFilterChange('premiumDiscountIndicator', value ?? '');
    },
    [handleFilterChange]
  );

  const onFraudDetectionChange = useCallback(
    (value?: string) => {
      setFraudDetectionLocal(value);
      handleFilterChange('fraudDetectionIndicator', value ?? '');
    },
    [handleFilterChange]
  );

  const onRowSelect = (row: any) => {
    navigateToMasterPoolDetails(row.original, i18n.language, history);
  };

  useEffect(() => {
    if (!filterValues?.endDate) {
      setToDateInput(null);
    }
    if (!filterValues?.startDate) {
      setFromDateInput(null);
    }
  }, [filterValues?.endDate, filterValues?.startDate]);

  const handleSearchAgain = () => {
    toggleSearchDrawer && toggleSearchDrawer(true, true);
  };

  const handleRequestTypeFilterChange = (
    filterType: string,
    selectedValue: DropdownOptionType | ComboboxOption | boolean | Date | string,
    item = ''
  ) => {
    if (filterType === 'requestType') {
      const values = filterValues?.requestType || [];
      const requestType = new Set(values as string[]);
      if (selectedValue) {
        requestType.add(item as string);
      } else {
        requestType.delete(item);
      }
      setFilterValues((prevState) => ({
        ...prevState,
        [filterType]: [...requestType]
      }));
    }
  };

  const handleAddModifyRemarksToastNotification = (
    error: boolean,
    message: { header: string; subHeader: string }
  ) => {
    if (error) {
      setShowErrorBannerNotification(true);
      setNotificationBannerConfig({
        header: message.header,
        subHeader: message.subHeader
      });
    } else {
      handleToastNotification('icon_check_circle', '', ' ');
      setToastNotificationConfig({
        header: message.header,
        subHeader: message.subHeader
      });
    }
  };

  const notificationBannerContent = showDataTableErrorBanner
    ? dataTableErrorBannerConfig
    : notificationBannerConfig;

  const showNotificationBanner = showErrorBannerNotification || showDataTableErrorBanner;

  const handleNotificationBannerClose = () => {
    if (showDataTableErrorBanner) {
      setIsDataTableErrorDismissed(true);
      return;
    }

    setShowErrorBannerNotification(false);
  };

  return (
    <div>
      <div>
        <FlyoutContainer
          id="portfolio-filter-flyout-container"
          isHeaderRequired={true}
          headerText={t('Filter')}
          open={openFlyout}
          onClose={() => {
            toggleDrawer(false);
            setFormErrors({});
            setFilterValues({
              ...activeFilters,
              startDate: activeFilters.startDate
                ? moment(activeFilters.startDate).toDate()
                : undefined,
              endDate: activeFilters.endDate ? moment(activeFilters.endDate).toDate() : undefined
            });
          }}
          content={
            <Box
              className={styles.flyoutContainer}
              role="presentation"
              sx={{
                pt: 3
              }}
            >
              <Box>
                <Accordion
                  accordionData={{
                    heading: t('Pi-Portfolio-Management-Table-Column-RequestReceiptDate'),
                    defaultExpanded: true,
                    contentSlot: (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: '24px'
                        }}
                      >
                        <DatePicker
                          value={
                            fromDateInput !== null && moment(fromDateInput).isValid()
                              ? fromDateInput
                              : filterValues?.startDate
                                ? new Date(filterValues.startDate)
                                : null
                          }
                          placeholderText={t('DateFormat')}
                          onChange={(value) => {
                            const cleared = value === null;

                            if (cleared) {
                              setFromDateInput(null);
                              setFilterValues((prev) => ({
                                ...prev,
                                startDate: undefined
                              }));
                              setActiveFilters((prev) => ({
                                ...prev,
                                startDate: undefined
                              }));
                            } else {
                              setFromDateInput(value);
                            }

                            if (!value && !toDateInput) {
                              setFormErrors({
                                ...formErrors,
                                fromDate: undefined,
                                toDate: undefined,
                                dateRange: undefined
                              });
                            }
                          }}
                          format="yyyy-MM-dd"
                          error={formErrors?.fromDate}
                          errorMessage={t(
                            'Pi-Portfolio-Management-Table-Filter-Date-Error-Message'
                          )}
                          label={t('Pi-Portfolio-Management-Table-Filter-Date-Start-Date')}
                          disableFuture
                        />
                        <DatePicker
                          value={
                            toDateInput !== null && moment(toDateInput).isValid()
                              ? toDateInput
                              : filterValues?.endDate
                                ? new Date(filterValues.endDate)
                                : null
                          }
                          placeholderText={t('DateFormat')}
                          onChange={(value) => {
                            const cleared = value === null;

                            if (cleared) {
                              setToDateInput(null);
                              setFilterValues((prev) => ({
                                ...prev,
                                endDate: undefined
                              }));
                              setActiveFilters((prev) => ({
                                ...prev,
                                endDate: undefined
                              }));
                            } else {
                              setToDateInput(value);
                            }

                            if (!value && !fromDateInput) {
                              setFormErrors({
                                ...formErrors,
                                toDate: undefined,
                                fromDate: undefined,
                                dateRange: undefined
                              });
                            }
                          }}
                          error={formErrors?.toDate || !!formErrors?.dateRange}
                          errorMessage={
                            formErrors?.dateRange ??
                            t('Pi-Portfolio-Management-Table-Filter-Date-Error-Message')
                          }
                          format="yyyy-MM-dd"
                          label={t('Pi-Portfolio-Management-Table-Filter-Date-End-Date')}
                          disableFuture
                        />
                      </Box>
                    )
                  }}
                ></Accordion>
                <Accordion
                  accordionData={{
                    heading: `${t('Pi-Portfolio-Management-Table-Column-InsuredApprovedLenderName')} ${filterValues.ialn?.length > 0 ? `(${filterValues.ialn?.length})` : ''}`,
                    defaultExpanded: true,
                    contentSlot: (
                      <Box>
                        <AutoCompleteDropDown
                          ariaText={{
                            value: t(
                              'Pi-Portfolio-Management-Table-Column-InsuredApprovedLenderName'
                            )
                          }}
                          value={filterValues.ialn?.map((item: DropdownOptionType) => item.value)}
                          onChange={(evt: MouseOrKeyboardEvent, option: ComboboxOption) => {
                            handleFilterChange('ialn', option);
                          }}
                          options={lenderList}
                          placeholderText={t('Select')}
                          error={formErrors.ialn}
                          errorMessage={formErrors.ialn ? 'Select valid lender name' : undefined}
                        />
                      </Box>
                    )
                  }}
                ></Accordion>
                <Accordion
                  accordionData={{
                    heading: `${t('Pi-Master-Pool-Inventory-Table-Column-Status')} ${filterValues.status?.length > 0 ? `(${filterValues.status?.length})` : ''}`,
                    defaultExpanded: true,
                    contentSlot: (
                      <Box>
                        <AutoCompleteDropDown
                          ariaText={{
                            value: t('Pi-Master-Pool-Inventory-Table-Column-Status')
                          }}
                          value={filterValues.status?.map((item: DropdownOptionType) => item.value)}
                          onChange={(evt: MouseOrKeyboardEvent, option: ComboboxOption) =>
                            handleFilterChange('status', option)
                          }
                          options={getFilterDropdownValues(Status, loanStatusMapping(t))}
                          placeholderText={t('Select')}
                          error={formErrors.status}
                          errorMessage={formErrors.status ? 'Select valid status' : undefined}
                        />
                      </Box>
                    )
                  }}
                ></Accordion>
                <Accordion
                  accordionData={{
                    heading: t('Pi-Master-Pool-Inventory-Table-Column-PremiumDiscount'),
                    defaultExpanded: true,
                    contentSlot: (
                      <Box>
                        <RadioButton
                          key={`premiumLocal-${premiumLocal ?? 'none'}`}
                          defaultOption={premiumLocal ?? undefined}
                          options={premiumDiscountOptions}
                          onChange={onPremiumChange}
                          errorMessage={
                            formErrors.premiumDiscountIndicator
                              ? t('PI-Master-Pool-Radio-Button-Error-Msg')
                              : undefined
                          }
                          noDisplayErrorMessage={!formErrors.premiumDiscountIndicator}
                          required={formErrors.premiumDiscountIndicator}
                        />
                      </Box>
                    )
                  }}
                ></Accordion>
                <Accordion
                  accordionData={{
                    heading: t('Fraud-Detection-Indicator'),
                    defaultExpanded: true,
                    contentSlot: (
                      <Box>
                        <RadioButton
                          key={`fraudDetectionLocal-${fraudDetectionLocal ?? 'none'}`}
                          defaultOption={fraudDetectionLocal ?? undefined}
                          options={fraudDetectionOptions}
                          onChange={onFraudDetectionChange}
                          errorMessage={
                            formErrors.fraudDetectionIndicator
                              ? t('PI-Master-Pool-Radio-Button-Error-Msg')
                              : undefined
                          }
                          noDisplayErrorMessage={!formErrors.fraudDetectionIndicator}
                          required={formErrors.fraudDetectionIndicator}
                        />
                      </Box>
                    )
                  }}
                ></Accordion>
                <Accordion
                  accordionData={{
                    heading: `${t('Pi-Portfolio-Management-Table-Column-RequestType')} ${filterValues.requestType?.length > 0 ? `(${filterValues.requestType?.length})` : ''}`,
                    defaultExpanded: true,
                    contentSlot: (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                        className={styles.requestTypeCheckboxContainer}
                      >
                        {RequestType.map((item, index) => {
                          return (
                            <Checkbox
                              key={index}
                              showCheckboxGroup={false}
                              checked={!!filterValues.requestType?.includes(item)}
                              onChange={(evt) =>
                                handleRequestTypeFilterChange(
                                  'requestType',
                                  evt.target.checked,
                                  item
                                )
                              }
                              label={{
                                value: getRequestTypeLabel(
                                  item as keyof typeof requestTypeMapping,
                                  t
                                )
                              }}
                              errorCheckbox={formErrors.requestType}
                            />
                          );
                        })}
                        {filterValues.requestType === undefined && formErrors.requestType && (
                          <Box
                            className={styles.errorContainer}
                            sx={{
                              display: 'flex',
                              paddingTop: '5px'
                            }}
                          >
                            <CmhcIcon
                              size={CmhcIconSize.SMALL}
                              type="icon_error_outlined"
                              className={styles.errorInfoIcon}
                            />
                            <span
                              className={styles.errorMessageText}
                              id="error-combobox"
                              role="alert"
                            >
                              {t('PI-Master-Pool-Radio-Button-Error-Msg')}
                            </span>
                          </Box>
                        )}
                      </Box>
                    )
                  }}
                ></Accordion>
                <Accordion
                  suppressBottomBorder
                  accordionData={{
                    heading: `${t('Pi-Master-Pool-Inventory-Table-Column-MasterPoolNumber')} ${filterValues.masterPoolNumber?.length > 0 ? `(${filterValues.masterPoolNumber?.length})` : ''}`,
                    defaultExpanded: true,
                    contentSlot: (
                      <Box>
                        <AutoCompleteDropDown
                          ariaText={{
                            value: 'Masterpool number'
                          }}
                          value={filterValues.masterPoolNumber?.map(
                            (item: DropdownOptionType) => item.value
                          )}
                          onChange={(evt: MouseOrKeyboardEvent, option: ComboboxOption) =>
                            handleFilterChange('masterPoolNumber', option)
                          }
                          options={masterPoolNumbersList}
                          placeholderText={t('Select')}
                          error={formErrors.masterPoolNumber}
                          errorMessage={
                            formErrors.masterPoolNumber
                              ? 'Select Valid Master pool number'
                              : undefined
                          }
                        />
                      </Box>
                    )
                  }}
                ></Accordion>
              </Box>
              <Box className={styles.flyoutFooter}>
                <Stack direction="row" spacing={3}>
                  <Button
                    type="button"
                    name={`btn`}
                    onClick={handleClearFilter}
                    className={styles.fullWidth}
                    variant={ButtonVariant.SECONDARY}
                    text={{
                      value: t('Pi-Portfolio-Management-Table-Filter-Date-Clear-Filters')
                    }}
                    ariaText={{
                      value: t('Pi-Portfolio-Management-Table-Filter-Date-Clear-Filters')
                    }}
                  />

                  <Button
                    type="submit"
                    name={`btn`}
                    onClick={() => handleFilterSubmit()}
                    className={styles.fullWidth}
                    variant={ButtonVariant.PRIMARY}
                    text={{
                      value: t('Pi-Portfolio-Management-Table-Filter-Date-Show-Results')
                    }}
                    ariaText={{
                      value: t('Pi-Portfolio-Management-Table-Filter-Date-Show-Results')
                    }}
                  />
                </Stack>
              </Box>
            </Box>
          }
        />
      </div>
      {showNotificationBanner && (
        <div className={styles.notificationBanner}>
          <NotificationBanner
            key={notificationBannerContent.header}
            showNotificationBanner={showNotificationBanner}
            onCloseNotificationBanner={handleNotificationBannerClose}
            headerText={{ value: notificationBannerContent.header }}
            message={{ value: notificationBannerContent.subHeader }}
            variant={NotificationBannerType.ERROR}
          />
        </div>
      )}
      {!!Object.values(searchQuery)[0] && !isLoading && dataType && (
        <Box className={styles.searchResultText}>
          {flatData.length === 0
            ? t('Search-Result-No-Data-subheader1')
            : flatData.length > 0
              ? t('Search-Result-No-Data-content1')
              : ''}
          {
            <>
              {t(`${getLabel(dataType)}`)}{' '}
              <data className={styles.queryContainer}>{Object.values(searchQuery ?? {})[0]}.</data>{' '}
              {t('Search-Result-No-Data-content2')}
              <Button
                name={t('Search-Result-No-Data-content3')}
                size={ButtonSize.SMALL}
                variant={ButtonVariant.TERTIARY}
                onClick={handleSearchAgain}
                text={{ value: t('Search-Result-No-Data-content3') }}
                ariaText={{ value: t('Search-Result-No-Data-content3') }}
              />
              {t('Search-Result-No-Data-content4')}
              {t('or')}
              <Button
                name={t('clear')}
                size={ButtonSize.SMALL}
                variant={ButtonVariant.TERTIARY}
                onClick={() => {
                  history.replace({
                    pathname: location.pathname
                  });
                }}
                text={t('clear').toLocaleLowerCase()}
                ariaText={t('clear')}
              />
              {t('search-results')}
            </>
          }
        </Box>
      )}
      {getActiveFilterChips().length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 2 }} className={styles.chipContainer}>
          {getActiveFilterChips().map((chip) => (
            <Chip
              key={chip.key}
              label={chip.label}
              className={styles.chipStyles}
              onDelete={() => handleChipDelete(chip)}
            />
          ))}
          <Button
            onClick={() => {
              handleClearFilter();
              setActiveFilters(initialFilterValues);
            }}
            variant={ButtonVariant.TERTIARY}
            name="Clear Filters"
            text={{
              value: t('Pi-Portfolio-Management-Table-Filter-Date-Clear-Filters')
            }}
            leadingIcon={() => <CmhcIcon size={CmhcIconSize.SMALL} type="icon_clear_circle" />}
            className={styles.clearAllFiltersButton}
          >
            {t('Pi-Portfolio-Management-Table-Filter-Date-Clear-Filters')}
          </Button>
        </Box>
      )}
      {!Object.values(searchQuery)[0] &&
        !isLoading &&
        flatData.length === 0 &&
        getActiveFilterChips().length > 0 && (
          <Box className={styles.noDataText}>
            {t('PI-Master-Pool-Premiums-Taxes-No-Data-Error-Header')}.
          </Box>
        )}
      <div className={styles.hbtMastePoolTableContainer}>
        <MRTDataTable<MasterPoolTableFields>
          data={flatData}
          columns={columns}
          enableRowActions
          onRowClick={onRowSelect}
          renderRowActions={({ row }) => (
            <RowActions
              row={row}
              setSelectedRow={setSelectedRow}
              toggleMasterPoolNumberRemarksDrawer={toggleMasterPoolNumberRemarksDrawer}
              handleToastNotification={handleToastNotification}
            />
          )}
          enableRowVirtualization={true}
          enableColumnVirtualization={true}
          localization={mrtCustomTranslation}
          pageSize={100}
          memoMode="cells"
          columnResizeMode="onChange"
          muiTableBodyRowProps={({ row }) => ({
            tabIndex: 0,
            onClick: () => onRowSelect(row),
            onKeyDown: (e) => {
              if (e.key === 'Enter') {
                onRowSelect(row);
              }
            }
          })}
          enableSorting={true}
          initialState={{
            columnPinning: { left: ['mrt-row-actions', 'latestRemark', 'requestReceiptDate'] }
          }}
          enablePagination={false}
          enableStickyHeader={true}
          enableSortingRemoval={false}
          enableBottomToolbar={false}
          muiTableContainerProps={{
            ref: tableContainerRef,
            sx: { maxHeight: '600px', minHeight: '300px' },
            onScroll: (event: UIEvent<HTMLDivElement>) =>
              fetchMoreOnBottomReached(event.target as HTMLDivElement)
          }}
          manualSorting
          onSortingChange={setSorting}
          state={{
            isLoading,
            sorting
          }}
          renderEmptyRowsFallback={() => <></>}
        />
      </div>
      <ViewHistoricalRemarksFlyout
        openFlyout={openHistoricalRemarksFlyout}
        toggleFlyout={() => {
          toggleHistoricalRemarksDrawer(false);
        }}
        selectedRowsData={selectedMasterPoolForRemark}
        remarksResponse={remarksResponse}
        isRemarksLoading={isRemarksLoading}
        isRemarksError={isRemarksError}
      />
      <HbtMasterPoolNumberAddRemarksFlyout
        masterPoolNumber={selectedRow.masterPoolNumber}
        latestRemark={selectedRow.latestRemark}
        toggleToaster={handleAddModifyRemarksToastNotification}
        openFlyout={openMasterPoolNumberRemarksFlyout}
        toggleDrawer={toggleMasterPoolNumberRemarksDrawer}
      />
      <ToastNotification
        icon={toastNotificationConfig.icon}
        dismissMode="autoAndActionable"
        variant={ToastNotificationVariant.INFO}
        open={showToast}
        header={{ value: toastNotificationConfig.header }}
        subHeader={{ value: toastNotificationConfig.subHeader }}
        onClose={() => {
          setShowToast(false);
        }}
      />
    </div>
  );
};

export default memo(HbtMasterPoolTable);
